import { useEffect, useState } from "react";
import { Wallet, Crown, Tag, FileDown } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatFCFA, formatDate } from "@/lib/format";

const catColor = (cat) => {
  if (cat === "Adhésions Club") return "bg-amber-100 text-amber-700";
  if (cat === "Ventes") return "bg-sky-100 text-sky-700";
  return "bg-slate-100 text-slate-600";
};

export default function Accounting() {
  const [summary, setSummary] = useState(null);
  const [revenues, setRevenues] = useState([]);
  const [filter, setFilter] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [exporting, setExporting] = useState(false);

  const load = (cat) => {
    api.get("/accounting/summary").then((r) => setSummary(r.data)).catch(() => {});
    const url = cat ? `/accounting/revenues?category=${encodeURIComponent(cat)}` : "/accounting/revenues";
    api.get(url).then((r) => setRevenues(r.data)).catch(() => {});
  };
  useEffect(() => load(""), []);

  const applyFilter = (cat) => { setFilter(cat); load(cat); };

  const exportExcel = async () => {
    setExporting(true);
    try {
      const params = [];
      if (start) params.push(`start=${start}`);
      if (end) params.push(`end=${end}`);
      const res = await api.get(`/accounting/export${params.length ? "?" + params.join("&") : ""}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `releve_revenus_${start || "debut"}_${end || "fin"}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Relevé Excel téléchargé");
    } catch {
      toast.error("Échec de l'export");
    } finally {
      setExporting(false);
    }
  };

  if (!summary) return <div className="text-slate-400">Chargement…</div>;

  return (
    <div className="max-w-7xl mx-auto space-y-6 animate-in-up" data-testid="accounting-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-1">Finances</p>
          <h1 className="font-display text-4xl font-bold tracking-tight text-slate-900">Comptabilité</h1>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Du</label>
            <input data-testid="export-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Au</label>
            <input data-testid="export-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" />
          </div>
          <button data-testid="export-excel-button" onClick={exportExcel} disabled={exporting} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm transition-colors duration-200 disabled:opacity-60">
            <FileDown className="h-4 w-4" /> {exporting ? "Export…" : "Exporter Excel"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg border border-slate-200 p-6" data-testid="acc-total">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400">Revenus totaux</span>
            <div className="h-9 w-9 rounded-lg bg-sky-50 flex items-center justify-center"><Wallet className="h-[18px] w-[18px] text-sky-600" /></div>
          </div>
          <p className="font-display text-3xl font-bold text-slate-900 tabular">{formatFCFA(summary.total)}</p>
        </div>
        <div className="bg-[#FFFBEB] rounded-lg border border-amber-200 p-6" data-testid="acc-club">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-bold uppercase tracking-[0.15em] text-amber-500">Adhésions Club</span>
            <div className="h-9 w-9 rounded-lg bg-amber-100 flex items-center justify-center"><Crown className="h-[18px] w-[18px] text-amber-600" /></div>
          </div>
          <p className="font-display text-3xl font-bold text-amber-800 tabular">{formatFCFA(summary.club_total)}</p>
          <p className="text-xs text-amber-600 mt-1">Frais Pass Privilège (2 000 FCFA)</p>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-6" data-testid="acc-categories">
          <span className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400">Catégories</span>
          <div className="mt-3 space-y-2">
            {summary.categories.map((c) => (
              <div key={c.category} className="flex items-center justify-between text-sm">
                <span className="text-slate-600">{c.category}</span>
                <span className="tabular font-semibold text-slate-800">{formatFCFA(c.amount)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={() => applyFilter("")} data-testid="filter-all" className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors duration-200 ${filter === "" ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>Tout</button>
        {summary.categories.map((c) => (
          <button key={c.category} onClick={() => applyFilter(c.category)} data-testid={`filter-${c.category}`} className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors duration-200 ${filter === c.category ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{c.category}</button>
        ))}
      </div>

      {/* Ledger */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100 text-left">
              <th className="px-6 py-3.5 font-semibold text-slate-500 text-xs uppercase tracking-wider">Date</th>
              <th className="px-6 py-3.5 font-semibold text-slate-500 text-xs uppercase tracking-wider">Libellé</th>
              <th className="px-6 py-3.5 font-semibold text-slate-500 text-xs uppercase tracking-wider">Catégorie</th>
              <th className="px-6 py-3.5 font-semibold text-slate-500 text-xs uppercase tracking-wider text-right">Montant</th>
            </tr>
          </thead>
          <tbody>
            {revenues.length === 0 && (
              <tr><td colSpan={4} className="px-6 py-12 text-center text-slate-400">Aucune écriture.</td></tr>
            )}
            {revenues.map((r) => (
              <tr key={r.id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors duration-150" data-testid={`revenue-row-${r.id}`}>
                <td className="px-6 py-4 tabular text-slate-500">{formatDate(r.date)}</td>
                <td className="px-6 py-4 text-slate-800">{r.label}</td>
                <td className="px-6 py-4">
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full inline-flex items-center gap-1 ${catColor(r.category)}`}>
                    <Tag className="h-3 w-3" /> {r.category}
                  </span>
                </td>
                <td className="px-6 py-4 text-right tabular font-semibold text-emerald-600">+ {formatFCFA(r.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
